/**
 * 補正サーバーの画像認識（Issue #706 / #707。TetherCam の設定の画面に置く）。
 *
 * - **席ごとに見本を登録**（Issue #707）: P1 のデッキ・P2 のデッキ・トークン（GetDeckCards の deck_id = -1）を選んで登録する。
 *   GetDeckCards の download_url（メディアの CloudFront の署名付き URL）を渡し、補正サーバーが S3 から取りに行く（ブラウザは画像を運ばない）。
 *   名前は `<code>:<画像の鍵>`（リアル再現と同じ。持っている名前は取りに行かない）。選んだデッキはこの端末に覚え、開き直したら
 *   登録し直さずに組を作り直す
 * - 一覧は P1 のデッキ / P2 のデッキ / トークン / そのほか（リアル再現が登録したものなど）に分けて出す（サムネイル・カード名・名前・特徴点）
 * - リアルタイムの確認: リアル再現と同じ知らせ（POST /live/recog/watch。NDJSON を開いたまま）を P1・P2 それぞれ（両方同時にも）開き、
 *   いちばん上の候補・点・絵の遅れを出す。補正サーバーが落ち着いたと言ったカード（行の stable。リアル再現が選択を切り替えるのはこれだけ。
 *   Issue #748）は上に大きく出す（1 枚ごとの順位はそのまま下に）。
 *   候補はその席のデッキ＋トークン（デッキを選んでいなければ登録されている全部）。知らせが来るのはいちばん上か stable が変わったときと 2 秒ごと（ping）
 * - 登録を消す: 1 枚ずつ・組ごと・全部（POST /live/recog/cards/delete）。押し間違いで消えないよう、2 回押して消す
 * - 合言葉の要らない口。fetch を持たない環境（テストの jsdom）では読まない。fetchImpl で差し替えられる
 */
import { liveUrl } from './liveBase';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api/api';

const THUMB_W = 60;
const REGISTER_BATCH = 20;    // 1 回の POST で渡す枚数（補正サーバーが取りに行くあいだ待つので小分けにする）
const TOKEN_DECK_ID = -1;     // トークンのデッキ（GetDeckCards）
const STORE_KEY = 'recogPanel.decks';

/** 溜まった文字列を行に分ける → [完全な行, 残り] */
export function splitLines(buf) {
  const parts = buf.split('\n');
  const rest = parts.pop();
  return [parts.filter((l) => l.trim()), rest];
}

/** 知らせの 1 行 → 画面の状態に重ねる */
export function applyLine(st, line, nowMs = Date.now()) {
  if (line.type === 'start') {
    return { ...st, phase: line.missing?.length ? 'missing' : 'watching', missing: line.missing ?? [], candidates: line.candidates };
  }
  if (line.type === 'result') {
    const times = [...(st.times ?? []).filter((t) => nowMs - t < 5000), nowMs];
    return { ...st, phase: 'watching', results: line.results ?? [], stable: line.stable ?? null, stableMeta: line.stable_meta ?? null,
      ageMs: line.frame_age_ms, error: null, times, lastAt: nowMs };
  }
  if (line.type === 'error') return { ...st, error: line.error };
  if (line.type === 'ping') return { ...st, lastPing: nowMs };
  return st;
}

/** GetDeckCards の 1 枚 → 補正サーバーに渡す見本（url。名前は画像の鍵） */
export function deckCardSample(c, deckName = null, players = []) {
  const code = String(c.card_id ?? '');
  if (!/^\d+$/.test(code) || Number(code) === 0 || !c.download_url) return null;
  const key = `${c.card_image_card_id || code}_${c.language_code || 'xx'}_${String(Number(c.image_index || 0)).padStart(2, '0')}`;
  return { name: `${Number(code)}:${key}`, url: c.download_url, meta: { code: Number(code), card_name: c.card_name || code, card_reading: c.card_reading ?? null, players, deck: deckName } };
}

/** デッキのカード → 見本（同じ名前は 1 つ） */
export function deckSamples(cards, deckName, players) {
  const seen = new Set();
  return (cards ?? []).map((c) => deckCardSample(c, deckName, players)).filter((x) => x && !seen.has(x.name) && seen.add(x.name));
}

/** 席の候補: その席のデッキ＋トークンのうち登録されているもの。デッキを選んでいなければ null（＝登録されている全部） */
export function seatCandidates(groups, seat, registered) {
  const own = groups[seat]?.names;
  if (!own?.length) return null;
  const have = new Set(registered);
  return [...new Set([...own, ...(groups.tokens?.names ?? [])])].filter((n) => have.has(n));
}

const thumbUrl = (name) => liveUrl(`/live/recog/card.jpg?name=${encodeURIComponent(name)}`);

function CardThumb({ name, width = THUMB_W }) {
  return <img src={thumbUrl(name)} alt="" width={width} style={{ display: 'block', background: '#111', borderRadius: 3 }} loading="lazy" />;
}

function loadStored() {
  try { return JSON.parse(localStorage.getItem(STORE_KEY) || '{}') || {}; } catch { return {}; }
}
function saveStored(v) {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(v)); } catch { /* 覚えられなくても動く */ }
}

/** 2 回押して実行するボタン（1 回目で「もう一度押すと…」に変わり、4 秒で戻る） */
function ConfirmButton({ label, confirmLabel, onConfirm, disabled = false, testId, small = false }) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return undefined;
    const t = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(t);
  }, [armed]);
  return (
    <button type="button" className="btn" disabled={disabled} data-testid={testId}
      style={small ? { fontSize: 11, padding: '1px 6px' } : undefined}
      onClick={() => { if (armed) { setArmed(false); onConfirm(); } else setArmed(true); }}>
      {armed ? confirmLabel : label}
    </button>
  );
}

function CardTile({ c, onDelete, busy }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', minWidth: 0, fontSize: 12 }} data-testid="recog-card">
      {c.thumb ? <CardThumb name={c.name} /> : <div style={{ width: THUMB_W, height: 86, background: '#111' }} />}
      <div style={{ minWidth: 0, display: 'grid', gap: 2 }}>
        <strong style={{ overflowWrap: 'anywhere' }}>{c.meta?.card_name ?? c.name}</strong>
        <span className="mono" style={{ color: '#b8c0d4', overflowWrap: 'anywhere' }}>{c.name}</span>
        <span style={{ color: '#8a93a8' }}>特徴点 {c.points ?? '?'}</span>
        <span><ConfirmButton small label="消す" confirmLabel="もう一度で消す" disabled={busy} onConfirm={() => onDelete([c.name])} testId={`recog-delete-${c.name}`} /></span>
      </div>
    </div>
  );
}

export default function RecogPanel({ fetchImpl = null }) {
  const [list, setList] = useState(undefined);
  const [filter, setFilter] = useState('');
  const [watches, setWatches] = useState({ 1: null, 2: null });   // 席 → 知らせの状態（null = 止まっている）。両方同時に開ける
  const [decks, setDecks] = useState([]);
  const [chosen, setChosen] = useState(() => ({ 1: '', 2: '', tokens: true, ...loadStored() }));
  const [groups, setGroups] = useState({});       // {1: {deck, names}, 2: …, tokens: {names}}
  const [reg, setReg] = useState(null);           // 登録の進み具合と結果
  const aborts = useRef({});
  const f = fetchImpl ?? (typeof fetch === 'function' ? fetch : null);
  const live = !!fetchImpl || !navigator.userAgent.includes('jsdom');

  const load = useCallback(async () => {
    if (!f) return;
    try {
      const r = await f(liveUrl('/live/recog/cards'), { cache: 'no-store' });
      setList(r.ok ? await r.json() : null);
    } catch { setList(null); }
  }, [f]);
  useEffect(() => { if (live) load(); }, [load, live]);
  useEffect(() => () => Object.values(aborts.current).forEach((a) => a?.abort()), []);
  useEffect(() => {
    if (!live) return;
    Promise.resolve(api.project('GetDeckList', { keys: ['id', 'name', 'enable', 'version'], conditions: {} }))
      .then((d) => setDecks((Array.isArray(d) ? d : d?.data ?? []).filter((x) => x.enable !== false && x.name !== 'Tokens' && x.id !== TOKEN_DECK_ID)))
      .catch(() => setDecks([]));
  }, [live]);

  /** 選んだデッキ（とトークン）のカード → 組と見本 */
  const collect = useCallback(async (sel) => {
    const out = { groups: {}, samples: [] };
    const add = async (key, deckId, deckName, players) => {
      const dc = await api.project('GetDeckCards', { deck_id: deckId });
      const s = deckSamples(dc?.cards, deckName, players);
      out.groups[key] = { deck: deckName, names: s.map((x) => x.name) };
      out.samples.push(...s);
    };
    for (const st of [1, 2]) {
      const d = decks.find((x) => String(x.id) === String(sel[st]));
      if (d) await add(st, d.id, d.name, [`P${st}`]);
    }
    if (sel.tokens) await add('tokens', TOKEN_DECK_ID, 'トークン', ['P1', 'P2']);
    return out;
  }, [decks]);

  // 開き直したとき: 覚えていたデッキから組だけ作り直す（登録はしない）
  useEffect(() => {
    if (!decks.length || (!chosen[1] && !chosen[2])) return;
    let alive = true;
    collect(chosen).then((o) => { if (alive) setGroups(o.groups); }).catch(() => {});
    return () => { alive = false; };
  }, [decks]);   // eslint-disable-line react-hooks/exhaustive-deps

  const register = async () => {
    if (!f) return;
    saveStored(chosen);
    setReg({ phase: 'loading', done: 0, total: 0 });
    try {
      const { groups: g, samples } = await collect(chosen);
      const seen = new Set();
      const uniq = samples.filter((x) => !seen.has(x.name) && seen.add(x.name));
      const sum = { added: 0, skipped: 0, unusable: 0, failed: 0 };
      setReg({ phase: 'sending', done: 0, total: uniq.length, ...sum });
      for (let i = 0; i < uniq.length; i += REGISTER_BATCH) {
        const r = await f(liveUrl('/live/recog/cards'), {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ cards: uniq.slice(i, i + REGISTER_BATCH) }),
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const b = await r.json();
        sum.added += b.added?.length ?? 0; sum.skipped += b.skipped?.length ?? 0;
        sum.unusable += b.unusable?.length ?? 0; sum.failed += Object.keys(b.failed ?? {}).length;
        setReg({ phase: 'sending', done: Math.min(i + REGISTER_BATCH, uniq.length), total: uniq.length, ...sum });
      }
      setGroups(g);
      setReg({ phase: 'done', done: uniq.length, total: uniq.length, ...sum });
      load();
    } catch (e) {
      setReg((st) => ({ ...(st ?? {}), phase: 'failed', error: e.message }));
    }
  };

  const [deleting, setDeleting] = useState(false);
  const remove = async (names) => {
    if (!f || !names.length) return;
    setDeleting(true);
    try {
      const r = await f(liveUrl('/live/recog/cards/delete'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ names }) });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const b = await r.json();
      setReg({ phase: 'deleted', count: b.deleted?.length ?? 0 });
    } catch (e) {
      setReg({ phase: 'failed', error: `消せませんでした: ${e.message}` });
    } finally {
      setDeleting(false);
      load();
    }
  };

  const cards = list?.cards ?? [];
  const byName = Object.fromEntries(cards.map((c) => [c.name, c]));
  const q = filter.trim();
  const match = (c) => !q || `${c.name} ${c.meta?.card_name ?? ''} ${c.meta?.code ?? ''}`.includes(q);
  const grouped = new Set(['1', '2', 'tokens'].flatMap((k) => groups[k]?.names ?? []));
  const sections = [
    ...[1, 2].filter((s) => groups[s]).map((s) => ({ key: `p${s}`, title: `P${s}: ${groups[s].deck}`, names: groups[s].names })),
    ...(groups.tokens ? [{ key: 'tokens', title: 'トークン', names: groups.tokens.names }] : []),
    { key: 'other', title: grouped.size ? 'そのほか（リアル再現が登録したものなど）' : '登録されている見本', names: cards.map((c) => c.name).filter((n) => !grouped.has(n)) },
  ].map((s) => ({ ...s, cards: s.names.map((n) => byName[n]).filter((c) => c && match(c)), missing: s.names.filter((n) => !byName[n]).length }))
    .filter((s) => s.cards.length || s.missing);

  const candidatesFor = (st) => seatCandidates(groups, st, cards.map((c) => c.name)) ?? cards.filter(match).map((c) => c.name);
  const setSeatWatch = (st, fn) => setWatches((w) => ({ ...w, [st]: typeof fn === 'function' ? fn(w[st]) : fn }));

  const stop = (st) => { aborts.current[st]?.abort(); aborts.current[st] = null; setSeatWatch(st, null); };
  const start = async (st) => {
    stop(st);
    const candidates = candidatesFor(st);
    if (!candidates.length || !f) return;
    const ctrl = new AbortController();
    aborts.current[st] = ctrl;
    setSeatWatch(st, { phase: 'connecting', seat: st, results: [], missing: [], times: [] });
    try {
      const r = await f(liveUrl('/live/recog/watch'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ seat: st, candidates }), signal: ctrl.signal,
      });
      if (!r.ok || !r.body) throw new Error(`HTTP ${r.status}`);
      const reader = r.body.getReader();
      const dec = new TextDecoder();
      let buf = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const [lines, rest] = splitLines(buf);
        buf = rest;
        lines.forEach((l) => {
          try { const line = JSON.parse(l); setSeatWatch(st, (w) => (w ? applyLine(w, line) : w)); } catch { /* 壊れた行は飛ばす */ }
        });
      }
      setSeatWatch(st, (w) => (w ? { ...w, phase: 'closed' } : w));
    } catch (e) {
      if (ctrl.signal.aborted) return;
      setSeatWatch(st, (w) => ({ ...(w ?? { seat: st }), phase: 'failed', error: e.message }));
    }
  };
  const anyWatching = !!(watches[1] || watches[2]);

  const PHASE = { connecting: 'つないでいます…', watching: '知らせを受けています', missing: '持っていない見本がある（閉じた）', closed: '補正サーバーが閉じました', failed: 'つなげませんでした' };
  const busy = reg?.phase === 'loading' || reg?.phase === 'sending';
  const box = { display: 'grid', gap: 8, padding: 10, border: '1px solid #223', borderRadius: 6 };

  return (
    <section className="card" data-testid="recog-panel" style={{ display: 'grid', gap: 12, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <h3 className="card-title" style={{ margin: 0 }}>画像認識</h3>
        <span style={{ fontSize: 13, color: '#b8c0d4' }} data-testid="recog-count">
          {list === undefined ? '読み込み中…' : list === null ? '補正サーバーに届きません' : `登録されている見本 ${list.count} 枚`}
        </span>
        <span style={{ flex: 1 }} />
        <button type="button" className="btn" onClick={load}>一覧を読み直す</button>
        <ConfirmButton label="全部消す" confirmLabel={`もう一度押すと ${cards.length} 枚消します`} disabled={!cards.length || deleting}
          onConfirm={() => remove(cards.map((c) => c.name))} testId="recog-delete-all" />
      </div>

      <div style={box}>
        <strong style={{ fontSize: 14 }}>席ごとに見本を登録</strong>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', fontSize: 13 }}>
          {[1, 2].map((s) => (
            <label key={s} style={{ display: 'inline-flex', gap: 4, alignItems: 'center', minWidth: 0 }}>
              P{s}
              <select id={`recog-deck-${s}`} value={chosen[s]} onChange={(e) => setChosen((c) => ({ ...c, [s]: e.target.value }))}
                data-testid={`recog-deck-${s}`} style={{ minWidth: 0, maxWidth: 220 }}>
                <option value="">デッキを選ぶ</option>
                {decks.map((d) => <option key={d.id} value={d.id}>{d.name}{d.version ? `（v${d.version}）` : ''}</option>)}
              </select>
            </label>
          ))}
          <label style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
            <input id="recog-tokens" type="checkbox" checked={!!chosen.tokens} onChange={(e) => setChosen((c) => ({ ...c, tokens: e.target.checked }))} data-testid="recog-tokens" />
            トークンも
          </label>
          <button type="button" className="btn" onClick={register} disabled={busy || (!chosen[1] && !chosen[2] && !chosen.tokens)} data-testid="recog-register">登録する</button>
        </div>
        {reg ? (
          <div style={{ fontSize: 12, color: reg.phase === 'failed' ? '#ff8a8a' : '#b8c0d4' }} data-testid="recog-register-status">
            {reg.phase === 'loading' ? 'デッキのカードを読んでいます…'
              : reg.phase === 'deleted' ? `${reg.count} 枚の登録を消しました`
              : reg.phase === 'failed' ? (reg.error?.startsWith('消せません') ? reg.error : `登録できませんでした: ${reg.error}`)
                : `${reg.phase === 'done' ? '登録しました' : `登録中 ${reg.done} / ${reg.total}`}（新しく ${reg.added}・持っていた ${reg.skipped}${reg.unusable ? `・特徴点が足りない ${reg.unusable}` : ''}${reg.failed ? `・取れなかった ${reg.failed}` : ''}）`}
          </div>
        ) : null}
        <div style={{ fontSize: 12, color: '#8a93a8' }}>画像は補正サーバーが S3 から取りに行きます（ブラウザは運びません）。</div>
      </div>

      <div style={box}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 14 }}>リアルタイムの確認</strong>
          <span style={{ flex: 1 }} />
          {anyWatching ? <button type="button" className="btn" onClick={() => { stop(1); stop(2); }} data-testid="recog-stop-both">両方止める</button>
            : <button type="button" className="btn" onClick={() => { start(1); start(2); }} data-testid="recog-start-both">両方始める</button>}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10 }}>
          {[1, 2].map((st) => {
            const w = watches[st];
            const n = candidatesFor(st).length;
            const rate = w?.times?.length ? (w.times.length / 5).toFixed(1) : '0';
            return (
              <div key={st} style={{ display: 'grid', gap: 6, fontSize: 13, alignContent: 'start', minWidth: 0 }} data-testid={`recog-watch-${st}`}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <strong>P{st}</strong>
                  <span style={{ color: '#b8c0d4' }} data-testid={`recog-candidates-${st}`}>
                    候補 {n} 枚（{groups[st] ? `P${st} のデッキ${groups.tokens ? '＋トークン' : ''}` : '登録されている全部（絞り込みのとおり）'}）
                  </span>
                  {w ? <button type="button" className="btn" onClick={() => stop(st)} data-testid={`recog-stop-${st}`}>止める</button>
                    : <button type="button" className="btn" onClick={() => start(st)} disabled={!n} data-testid={`recog-start-${st}`}>始める</button>}
                </div>
                {w ? (
                  <>
                    <div style={{ color: w.phase === 'watching' && !w.error ? '#9be7a1' : '#f0c36b' }}>
                      {PHASE[w.phase] ?? w.phase}
                      {w.phase === 'watching' ? `・知らせ ${rate} 回/秒${w.ageMs != null ? `・絵の遅れ ${w.ageMs} ms` : ''}` : ''}
                      {w.error ? `・${w.error}` : ''}
                      {w.missing?.length ? `（${w.missing.length} 枚）` : ''}
                    </div>
                    {w.stable ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 700, color: '#9be7a1' }} data-testid={`recog-stable-${st}`}>
                        <CardThumb name={w.stable} width={48} />
                        <span>{w.stableMeta?.card_name ?? byName[w.stable]?.meta?.card_name ?? w.stable}</span>
                        <span style={{ fontSize: 12, fontWeight: 400, color: '#b8c0d4' }}>落ち着いた当たり</span>
                      </div>
                    ) : null}
                    {w.results?.length ? (
                      <ol style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 4 }}>
                        {w.results.slice(0, 5).map((r, i) => (
                          <li key={r.name} style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: i === 0 ? 700 : 400 }}>
                            <CardThumb name={r.name} width={40} />
                            <span>{r.meta?.card_name ?? byName[r.name]?.meta?.card_name ?? r.name}</span>
                            <span style={{ color: '#b8c0d4' }}>点 {r.score}・{r.role === 'recog2' ? '認識 2' : '認識 1'}</span>
                          </li>
                        ))}
                      </ol>
                    ) : w.phase === 'watching' ? <div style={{ color: '#8a93a8' }}>まだ何も当たっていません（認識領域にカードを置く）</div> : null}
                  </>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      <input id="recog-filter" type="search" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="カード名・code・名前で絞り込む"
        style={{ minWidth: 0 }} data-testid="recog-filter" />
      {sections.length ? sections.map((s) => (
        <div key={s.key} style={{ display: 'grid', gap: 8 }} data-testid={`recog-section-${s.key}`}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 13 }}>{s.title}（{s.cards.length} 枚{s.missing ? `・補正サーバーに無い ${s.missing} 枚` : ''}）</strong>
            {s.cards.length ? <ConfirmButton small label="この組を消す" confirmLabel={`もう一度押すと ${s.cards.length} 枚消します`} disabled={deleting}
              onConfirm={() => remove(s.cards.map((c) => c.name))} testId={`recog-delete-${s.key}`} /> : null}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 8 }}>
            {s.cards.map((c) => <CardTile key={c.name} c={c} onDelete={remove} busy={deleting} />)}
          </div>
        </div>
      )) : list ? <div style={{ fontSize: 13, color: '#8a93a8' }}>見本はまだありません（上でデッキを選んで登録するか、リアル再現で試合を開く）</div> : null}
    </section>
  );
}
